import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import BreakDownFormPanel1 from "../component/forms/BreakDownFormPanel1";
import {Formik} from "formik";
import * as Yup from "yup";
import { policyYearBreakDown } from './tabs/CaseDetailsInitialValues';
import { parseAndFormat} from "../../../../utils/commonUtils";
import { addNumbers, getBankValues} from "../../../../utils/commonFormulas";
import moment from "moment";
import { REQUIRED_FIELD } from "../../../../constants";
import { useActions } from "../../../../actions";
import * as TrancheActions from "../../../../actions/tranche";
import {TrancheRate} from "../../../../model/tranche";
import {getCurrentPolicyYear, nextAnniversaryDate} from "../../../clientServicing/ClientServiceDashbaord";
import numeral from "numeral";

const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                width: "100%",
                float: "left",
            },
            heading: {
                flexBasis: "20%",
                flexShrink: 0,
                marginBottom: 0,
            },
            label: {
                flexBasis: "25%",
                flexShrink: 0,
                marginBottom: 0,
            },
            secondaryHeading: {
                flexBasis: "30%",
                color: theme.palette.text.secondary,
                marginBottom: 0,
            },
            labelBtn: {
                padding: "2px 5px",
                fontSize: 12,
                borderRadius: 6,
                color:'#ffffff'
            },
            bgGreen: {
                background: "#3DDB93",
            },
            bgRed: {
                background: "#ff1744",
            },
            bgOrange: {
                background: "#FFA500",
            },
        }),
);

interface  PolicyBreakDownExpansionPanelProps {
    year:number, index:number, policyArr:any, setPolicyArr:Function, setRefresh:Function,
    yearPolicyBreakup:any, setPolicyFormDirty:Function, fetchCaseDetail:any, agePerYear:number, issueAge: number | undefined, setPolicyBreakDownExpanded: Function,
    caseDetails: any, policyStartYear: number, combineClientObj: {key: number, name: string, title: string}
    paidInFull?:boolean, formRef?:any, calculatedNewValues?: any
}

const PolicyBreakDownExpansionPanel1 = (props: PolicyBreakDownExpansionPanelProps) => {
    const {year, index, policyArr, setPolicyArr, setRefresh, yearPolicyBreakup, setPolicyFormDirty, fetchCaseDetail, agePerYear, issueAge, setPolicyBreakDownExpanded
    , caseDetails, policyStartYear, combineClientObj, calculatedNewValues } = props;
    const trancheActions = useActions(TrancheActions as any);
    const [initialValue, setInitialValue] = React.useState<any>(policyYearBreakDown)
    const classes = useStyles();
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [outStandingBalance, setOutStandingBalance] = React.useState<number>(0);
    const paymentFrequency = caseDetails && caseDetails.paymentFrequency || "annual";
    const bankValues = getBankValues(yearPolicyBreakup, index, caseDetails, year);
    const issueDate = caseDetails && caseDetails.tranche && caseDetails.tranche.issueDate;
    const anniversaryDate = nextAnniversaryDate(issueDate);
    const currentPolicyYear = getCurrentPolicyYear(anniversaryDate);
    const trancheList = caseDetails && caseDetails.tranche;
    const currentYearRate = trancheList && trancheList.rates && (trancheList.rates as TrancheRate[]).find(i => Number(i.policyYear) === currentPolicyYear);
    const trancheRate = currentYearRate && currentYearRate.rate ? numeral(currentYearRate.rate).value() : 0;


    React.useEffect(()=>{
        if(year === policyStartYear){
            setExpanded(`panel${year}`);
        }
    }, []);

    

    React.useEffect(() => {
        if(yearPolicyBreakup.clientContributionReceived && yearPolicyBreakup.clientContributionDue && Number(yearPolicyBreakup.clientContributionReceived) >= Number(yearPolicyBreakup.clientContributionDue)){
            setOutStandingBalance(0)
        } else {
            setOutStandingBalance((Number(yearPolicyBreakup.clientContributionDue) - Number(yearPolicyBreakup.clientContributionReceived)) || 0)
        }
    }, [yearPolicyBreakup, index]);
    React.useEffect(() => {
        let formValues = {
            /* Main */
            currentAge: agePerYear,
            adminClientAllocations: (yearPolicyBreakup && yearPolicyBreakup.adminClientAllocations && yearPolicyBreakup.adminClientAllocations.length > 0) ?
                    yearPolicyBreakup.adminClientAllocations : [
                        {
                            id: undefined,
                            allocationId: "",
                            indexAllocationPercent: "",
                            indexCreditAmount: "",
                            indexCreditPercent: "",
                            indexBonusAmount: "",
                            indexCurrentValue:"",
                            indexLock:"",
                            indexCap:"",
                            indexParticipationRate:""
                        },
                    ],
            statementAllocations: (yearPolicyBreakup && yearPolicyBreakup.statementAllocations && yearPolicyBreakup.statementAllocations.length > 0) ?
                    yearPolicyBreakup.statementAllocations : [
                        {
                            id: undefined,
                            allocationId: "",
                            indexAllocationPercent: "",
                            indexCreditAmount: "",
                            indexCreditPercent: "",
                            indexBonusAmount: "",
                            indexCurrentValue:"",
                            indexLock:"",
                            indexCap:"",
                            indexParticipationRate:""
                        },
                    ],       

            /* Illustrate Value */
            loanRate: (yearPolicyBreakup && yearPolicyBreakup.loanRate) || "",
            illustratedGrowthRate: (yearPolicyBreakup && yearPolicyBreakup.illustratedGrowthRate) || "",

            /* Actual Values */
            bankInterestRate: trancheRate,
            bankInterestRateUpdated: yearPolicyBreakup && yearPolicyBreakup.bankInterestRateUpdated,
            bankLoanBasis: bankValues.bankLB,
            bankLoanBasisUpdated: yearPolicyBreakup && yearPolicyBreakup.bankLoanBasisUpdated,
            dailyInterestAccrual: bankValues.dailyIA,
            interestAccruedYTD: bankValues.intAccruedYTD,
            loanBalanceYTD: yearPolicyBreakup && yearPolicyBreakup.loanBalanceYTD ? yearPolicyBreakup.loanBalanceYTD : addNumbers(bankValues.bankLB, bankValues.intAccruedYTD),
            actualNetSurrenderValue: parseAndFormat(Number(yearPolicyBreakup.actualSurrenderValue) - Number(yearPolicyBreakup.estLoanBalance)),
            netDeathBenefitActual: parseAndFormat(Number(yearPolicyBreakup.actualCurrentDeathBenefit) - Number(yearPolicyBreakup.estLoanBalance)),
            growthPercent: (calculatedNewValues && parseAndFormat(calculatedNewValues.policyGrowthRate)) || "",
            growthRateAverage: (calculatedNewValues && Number(calculatedNewValues.averageGrowthRate)) || "",
            loanRateAverage: (calculatedNewValues && calculatedNewValues.loanRateAverage) || "",
            paymentFrequency: paymentFrequency.charAt(0).toUpperCase() + paymentFrequency.slice(1)
        };
        if (yearPolicyBreakup) {
            Object.assign(formValues, {
                deathBenefitReduction: yearPolicyBreakup.deathBenefitReduction < 0 ? "0.00" : parseAndFormat(yearPolicyBreakup.deathBenefitReduction),
                revisedDeathBenefitAmount: parseAndFormat(yearPolicyBreakup.revisedDeathBenefitAmount),
                decliningDB: yearPolicyBreakup.decliningDB,
                decliningDBOther: yearPolicyBreakup.decliningDBOther,
                dbNotes: yearPolicyBreakup.dbNotes,
                illustratedPremium: parseAndFormat(yearPolicyBreakup.illustratedPremium),
                bankPremium: parseAndFormat(yearPolicyBreakup.bankPremium),
                projectedInterest: parseAndFormat(yearPolicyBreakup.projectedInterest),
                estLoanBalance: parseAndFormat(yearPolicyBreakup.estLoanBalance),
                arrangementFee : yearPolicyBreakup && parseAndFormat(yearPolicyBreakup.arrangementFee) || "",
                illustratedSurrenderValue: Number(yearPolicyBreakup.illustratedSurrenderValue) ? parseAndFormat(Number(yearPolicyBreakup.illustratedSurrenderValue)) : parseAndFormat(Number(yearPolicyBreakup.netCashValueIllustrated)*0.9),
                netCashValueIllustrated: parseAndFormat(yearPolicyBreakup.netCashValueIllustrated),
                netLoanCashValueIllustrated: parseAndFormat(yearPolicyBreakup.netLoanCashValueIllustrated),
                illustratedCurrentDeathBenefit: parseAndFormat(yearPolicyBreakup.illustratedCurrentDeathBenefit),
                illustratedNetDeathBenefit: parseAndFormat(yearPolicyBreakup.illustratedNetDeathBenefit),
                lowPoint: parseAndFormat(yearPolicyBreakup.lowPoint),
                insurancePremiumActual: parseAndFormat(yearPolicyBreakup.insurancePremiumActual),
                amountPaidToPolicy: parseAndFormat(calculatedNewValues && calculatedNewValues.cumulativeAmountPaidToPolicy) || "",
                bankLoanPremiumDue: bankValues.bLPD,
                bankLoanPremiumDueUpdated: yearPolicyBreakup.bankLoanPremiumDueUpdated,
                bankLoanAmount: yearPolicyBreakup.bankLoanAmount ? parseAndFormat(yearPolicyBreakup.bankLoanAmount) : addNumbers(bankValues.bankLB, bankValues.actualIA),
                loanAmount: parseAndFormat(yearPolicyBreakup.loanAmount),
                actualCurrentSurrenderValue: parseAndFormat(yearPolicyBreakup.actualCurrentSurrenderValue),
                amountPaidToClient: parseAndFormat(yearPolicyBreakup.amountPaidToClient),
                dateOfSurrender: yearPolicyBreakup.dateOfSurrender ? moment(yearPolicyBreakup.dateOfSurrender).format("YYYY-MM-DD") : "",
                perDiem: parseAndFormat(yearPolicyBreakup.perDiem),

                surrenderCompleted: yearPolicyBreakup.surrenderCompleted || "",
                surrenderCashOutAction: yearPolicyBreakup.surrenderCashOutAction || "",
                reasonForChange: yearPolicyBreakup.reasonForChange || "",

                actualSurrenderValue: parseAndFormat(yearPolicyBreakup.actualSurrenderValue),
                actualCurrentDeathBenefit: parseAndFormat(yearPolicyBreakup.actualCurrentDeathBenefit),
                growthAmount: parseAndFormat(calculatedNewValues && calculatedNewValues.growthDollarAmountEarned) || "",
                actualLowPoint: parseAndFormat(yearPolicyBreakup.actualSurrenderValue * 0.95),
                actualInterestAmount: bankValues.actualIA,
                cumulativeBankLoanPremiumPaid: parseAndFormat(calculatedNewValues && calculatedNewValues.cumulativeBankLoanPremiumPaid) || "",
                cumulativeEmployerContributionPaid: parseAndFormat(calculatedNewValues && calculatedNewValues.cumulativeEmployerContributionPaid) || "",
                cumulativeContributionPaid: parseAndFormat(calculatedNewValues && calculatedNewValues.cumulativeClientContributionPaid) || "",
                /* Statement */
                stmCumulativePaidToPolicy: parseAndFormat(yearPolicyBreakup && yearPolicyBreakup.stmCumulativePaidToPolicy) || "",
                stmSurrenderValue: parseAndFormat(yearPolicyBreakup && yearPolicyBreakup.stmSurrenderValue) || "",
                stmPremium: parseAndFormat(yearPolicyBreakup && yearPolicyBreakup.stmPremium) || "",
                stmDeathBenefit: parseAndFormat(yearPolicyBreakup && yearPolicyBreakup.stmDeathBenefit) || "",
                stmTotalCreditedAmount: parseAndFormat(yearPolicyBreakup.stmTotalCreditedAmount) || "",
                stmTotalValue: parseAndFormat(yearPolicyBreakup.stmTotalValue) || "",
                stmWeightedGrowthRate: parseAndFormat(yearPolicyBreakup.stmWeightedGrowthRate) || "",
                netStmSurrenderValue: parseAndFormat(Number(yearPolicyBreakup && yearPolicyBreakup.stmSurrenderValue) - Number(yearPolicyBreakup.estLoanBalance)) || "",
                netStmDeathBenefits: parseAndFormat(Number(yearPolicyBreakup && yearPolicyBreakup.stmDeathBenefit) - Number(yearPolicyBreakup.estLoanBalance)) || "",
            });
        }
        setInitialValue(formValues);
    }, [expanded, yearPolicyBreakup && yearPolicyBreakup.id, yearPolicyBreakup && yearPolicyBreakup.adminClientAllocations, yearPolicyBreakup && yearPolicyBreakup.statementAllocations, issueAge]);

    const handlePanelChange = (panel: string, year: number) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setPolicyBreakDownExpanded({panel, policyYear: year, clientPolicyId: caseDetails.id, isExpanded : isExpanded ? panel : false});
        setExpanded(isExpanded ? panel : false);
    };

    let keysValidation = {
                indexAllocationPercent: Yup.string().matches(/^\d{1,3}$/, "Must be a number with up to 3 digits").nullable(),
                indexCreditAmount: Yup.string().min(0, "Amount cannot be negative"),
                indexBonusAmount: Yup.string().min(0, "Amount cannot be negative"),
                indexCreditPercent: Yup.string().matches(/^\d{1,3}$/, "Must be a number with up to 3 digits").nullable(),
                surrenderCashOutAction: Yup.number().required(REQUIRED_FIELD),
                reasonForChange: Yup.string().required(REQUIRED_FIELD),
    }

    const policyBreakDownSchema = Yup.object().shape(keysValidation);

    return (
            <React.Fragment>
                <ExpansionPanel expanded={expanded === `panel${year}`}
                                onChange={handlePanelChange(`panel${year}`, year)}>
                    <ExpansionPanelSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="panel1bh-content"
                            id="panel1bh-header"
                    >
                        <Typography
                                className={classes.heading}
                                variant="body1">{year} - {year+1}&nbsp;(Year {index + 1})</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                        <Formik
                                innerRef={props.formRef}
                                initialValues={initialValue}
                                enableReinitialize={true}
                                validationSchema={policyBreakDownSchema}
                                validateOnChange={true}
                                onSubmit={async (values, { setSubmitting }) => {
                                    setTimeout(() => {
                                        setSubmitting(false);
                                    }, 500);
                                }}
                        >
                            {({
                                  handleSubmit
                              }) => (<form className="floatLeft w100" onSubmit={handleSubmit}>
                                {
                                    expanded &&
                                    <BreakDownFormPanel1
                                            expanded={expanded}
                                            policyArrIndex={index}
                                            year={year}
                                            setPolicyArr={setPolicyArr}
                                            policyArr={policyArr}
                                            setRefresh={setRefresh}
                                            setPolicyFormDirty={setPolicyFormDirty}
                                            yearPolicyBreakup={yearPolicyBreakup}
                                            outStandingBalance={outStandingBalance}
                                            fetchCaseDetail={fetchCaseDetail}
                                            combineClientObj={combineClientObj}
                                            caseDetails={caseDetails}
                                    />
                                }
                            </form>)}
                        </Formik>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </React.Fragment>
    );
};

export default PolicyBreakDownExpansionPanel1;